import { ReactNode, useEffect, useRef } from 'react';
import { Spinner } from '@/components/ui/Spinner';

interface InfiniteScrollListProps {
    /** Whether there are more pages to load. */
    hasNextPage: boolean;
    /** Whether the next page is currently being fetched. */
    isFetchingNextPage: boolean;
    /** Callback to load the next page. */
    onLoadMore: () => void;
    /** List content (items to display). */
    children: ReactNode;
    /** Additional CSS classes for the container. */
    className?: string;
    /** Optional loading text. */
    loadingText?: string;
}

/**
 * Infinite scroll container that uses IntersectionObserver
 * to trigger loading more items when the sentinel element
 * becomes visible in the viewport.
 */
export function InfiniteScrollList({
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    children,
    className = '',
    loadingText = 'Loading more...',
}: InfiniteScrollListProps) {
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasNextPage) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry && entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
                    onLoadMore();
                }
            },
            {
                root: null,
                rootMargin: '100px', // Trigger slightly before reaching the bottom.
                threshold: 0,
            }
        );

        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasNextPage, isFetchingNextPage, onLoadMore]);

    return (
        <div className={className}>
            {children}

            {/* Sentinel element for intersection observer */}
            <div ref={sentinelRef} className="h-1" aria-hidden="true" />

            {/* Loading indicator */}
            {isFetchingNextPage && (
                <div className="flex items-center justify-center py-4">
                    <Spinner className="mr-2" />
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                        {loadingText}
                    </span>
                </div>
            )}
        </div>
    );
}
